import { observer } from 'mobx-react';
import { ChangeEvent, FC, ReactElement, useCallback, useMemo } from 'react';
import { webhookEditStore } from '../../store';
import { API_METHOD, LOCALES } from '~/constants/main';
import { localeStore } from '~/mobx/localesStore';
import Input from '~/view/components/Controls/InputComponent/Input';
import Picker from '~/view/components/Controls/Pickers/Picker/Picker';
import { IPickerOption } from '~/view/components/Controls/Pickers/Picker/types';
import TabPicker from '~/view/components/Controls/Pickers/TabPicker/TabPicker';
import Textarea from '~/view/components/Controls/Textarea/Textarea';
import Text, { TEXT } from '~/view/components/Text/Text';
import './style.scss';

const WebhookEditMainBlock: FC = observer((): ReactElement => {
  const changeName = useCallback((value: string | undefined) => {
    webhookEditStore.changeWebhook({ name: value || '' });
  }, []);

  const changeUrl = useCallback((event: ChangeEvent<HTMLTextAreaElement>) => {
    webhookEditStore.changeWebhook({ url: event.target.value || '' });
  }, []);

  const changeMethod = useCallback((method: API_METHOD) => {
    webhookEditStore.changeWebhook({ method });
  }, []);

  const changeLanguage = useCallback((language: LOCALES) => {
    webhookEditStore.changeWebhook({ language });
  }, []);

  const changeType = useCallback((options: IPickerOption[]) => {
    if (!options.length) return;
    webhookEditStore.changeWebhook({ type: options[0].value });
  }, []);

  const methodPickerOptions = useMemo((): IPickerOption[] => {
    return [
      { value: API_METHOD.POST, label: API_METHOD.POST },
      { value: API_METHOD.PUT, label: API_METHOD.PUT },
    ];
  }, []);

  const languagePickerOptions = useMemo((): IPickerOption[] => {
    return [
      { value: LOCALES.EN, label: localeStore.t('english') },
      { value: LOCALES.AR, label: localeStore.t('arabic') },
    ];
  }, []);

  const typePickerOptions = useMemo((): IPickerOption[] => {
    return [{ value: 'order_status', label: localeStore.t('order_status') }];
  }, []);

  const typePickerSelectedOption = useMemo((): IPickerOption[] => {
    return [{ value: webhookEditStore.webhook.type, label: localeStore.t(webhookEditStore.webhook.type) }];
  }, [webhookEditStore.webhook.type]);

  return (
    <div className="d-flex flex-row gap-40">
      <div className="d-flex flex-column gap-15">
        <div className="d-flex flex-row">
          <Text type={TEXT.ORDER_FIELD} text={localeStore.t('name')} required className="webhook-edit-main__field-title" />
          <Input
            value={webhookEditStore.webhook.name}
            onChangeValue={changeName}
            validation={webhookEditStore.validation.name}
            placeholder={localeStore.t('Value')}
          />
        </div>
        <div className="d-flex flex-row">
          <Text type={TEXT.ORDER_FIELD} text={localeStore.t('ksk_url_placeholder')} required className="webhook-edit-main__field-title" />
          <Textarea value={webhookEditStore.webhook.url} onChange={changeUrl} validation={webhookEditStore.validation.url} />
        </div>
        <div className="d-flex flex-row">
          <Text type={TEXT.ORDER_FIELD} text={localeStore.t('method')} required className="webhook-edit-main__field-title" />
          <TabPicker value={webhookEditStore.webhook.method} options={methodPickerOptions} onChange={changeMethod} tabClassName="text-uppercase" />
        </div>
      </div>

      <div className="d-flex flex-column gap-15">
        <div className="d-flex flex-row">
          <Text type={TEXT.ORDER_FIELD} text={localeStore.t('language')} required className="webhook-edit-main__field-title" />
          <TabPicker value={webhookEditStore.webhook.language} options={languagePickerOptions} onChange={changeLanguage} />
        </div>
        <div className="d-flex flex-row">
          <Text type={TEXT.ORDER_FIELD} text={localeStore.t('txt_waitlist_type')} className="webhook-edit-main__field-title" />
          <Picker selectedOptions={typePickerSelectedOption} options={typePickerOptions} onSelect={changeType} noReset />
        </div>
      </div>
    </div>
  );
});

export default WebhookEditMainBlock;
